import { BodyLarge, BodyM, Button, Heading1 } from '@casavo/base-ui'
import useTranslation from 'next-translate/useTranslation'
import getConfig from 'next/config'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useSessionStorage } from 'usehooks-ts'

import { ConceptImage } from '@/components/ConceptImage'
import { Layout } from '@/components/Layout'
import { MobileBottomContainerFixed } from '@/components/MobileBottomContainerFixed'
import { MobileBottomMenu } from '@/components/MobileBottomMenu'
import { ModalBase } from '@/components/Modal/ModalBase'
import { Translated } from '@/components/Translated'
import { useAuthentication } from '@/context/AuthProvider'
import { useBottomSheet } from '@/context/BottomSheetContext'
import { useDevice } from '@/context/Device'
import { useModal } from '@/context/ModalContext'
import { Divider } from '@/design-lib/components/Divider'
import Icon from '@/design-lib/components/Icons'
import { ToastError } from '@/design-lib/components/Toast/ToastError'
import { ToastSuccess } from '@/design-lib/components/Toast/ToastSuccess'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { suggestedFeedSettingsPathSegment } from '@/i18n-routes'
import { SuggestedCriteria } from '@/lib/api-gluglu-auth'
import { useListingsAndPrioritiesBySuggestedCriteria } from '@/page-commons/listingsBySuggestedCriteria'
import { UserValuation } from '@/pages/profile/suggested'
import { isStorageSupported } from '@/utils/isStorageSupported'
import { isLocaleEnabledFor } from '@/utils/locale-variables'
import { buildRelativeLocalizedLink } from '@/utils/navigation'
import { GTMEventCategory } from '@/utils/tracking/eventCategory'
import { handleClick, handleShow } from '@/utils/tracking/eventsHandlers'

import { CriteriaList } from './CriteriaList'
import InferredCriteriaBanner from './InferredCriteriaBanner'
import { ListingsList } from './Sections/ListingsList'
import { PotentialPropertiesList } from './Sections/PotentialPropertiesList'
import { greetingTitle, propertiesListButton, suggestedListingPriceTrigger, suggestedPage } from './styles.css'
import { SuggestedListingPrice } from './SuggestedListingPrice'
import { MortgageBannerValuationPageContent } from './SuggestedListingPrice/MortgageBannerValuation/PageContent'

type Props = {
  criteria: SuggestedCriteria
  userValuation?: UserValuation
  userValuationSessionId?: string
}

const parseSuggestedCriteria = (storedCriteria: SuggestedCriteria) => {
  return {
    geoSlugs: storedCriteria?.geo_slugs,
    priceMax: storedCriteria?.budget,
    roomsMin: storedCriteria?.rooms_min,
  }
}

export const PropertiesList: React.FC<Props> = ({ criteria, userValuation, userValuationSessionId }) => {
  const {
    data = { listings: [], potentialProperties: [] },
    error,
    isLoading,
  } = useListingsAndPrioritiesBySuggestedCriteria(parseSuggestedCriteria(criteria))

  const {
    publicRuntimeConfig: { imageBasePath },
  } = getConfig()

  const { user } = useAuthentication()
  const { lang, t } = useTranslation()
  const { listings: listingsDetailCate = [], potentialProperties = [] } = { ...data }

  const hasCriteriaProvided = criteria.origin === 'USER_PROVIDED'
  const hasCriteriaInferred = criteria.origin === 'INFERRED'
  const router = useRouter()
  const [showToastError, setShowToastError] = useState<boolean>(!!error)
  const [showToastSuccess, setShowToastSuccess] = useState<boolean>(!!error)
  const [isMainSearchCreated, setMainSearchCreated] = useState<boolean>(false)
  const toastSuccessTitle = isMainSearchCreated
    ? t('suggested:Suggested_SuccessToast_Title')
    : t('suggested:Suggested_UpdateToast_Title')

  const device = useDevice()

  const { closeModal, isOpen: isModalOpen, openModal } = useModal()
  const { closeBottomSheet, isBottomSheetOpen, openBottomSheet } = useBottomSheet()
  const [storedUserValuation, setStoredUserValuation] = useSessionStorage('user_valuation', null)

  const handleSuggestedListingPriceClick = () => {
    if (userValuation || storedUserValuation) {
      const currentValuationData = userValuation || storedUserValuation
      if (device === 'mobile' && !isBottomSheetOpen) {
        openBottomSheet(
          <SuggestedListingPrice
            userValuation={userValuation}
            userValuationSessionId={userValuationSessionId}
            onSuggestedListingPriceClickHandler={() => {
              setStoredUserValuation(currentValuationData)
              closeBottomSheet()
            }}
          />,
          { bgColor: 'white', minHeight: 514 }
        )
      } else if (!isModalOpen) {
        openModal(
          <ModalBase
            noPadding
            handleClose={() => {
              setStoredUserValuation(currentValuationData)
            }}
          >
            <SuggestedListingPrice
              userValuation={userValuation}
              userValuationSessionId={userValuationSessionId}
              onSuggestedListingPriceClickHandler={() => {
                setStoredUserValuation(currentValuationData)
                closeModal()
              }}
            />
          </ModalBase>,
          { desktopWidth: 700, overlay: true }
        )
      }
    }
  }

  useEffect(() => {
    handleShow(GTMEventCategory.SuggestedPage, 'Show')

    if (userValuation) {
      if (JSON.stringify(userValuation) !== JSON.stringify(storedUserValuation)) {
        handleSuggestedListingPriceClick()
      }
    }
  }, [])

  if (isStorageSupported(() => sessionStorage) && sessionStorage.getItem('CASAVO@MAIN_SEARCH')) {
    setShowToastSuccess(true)

    if (sessionStorage.getItem('CASAVO@MAIN_SEARCH') === 'search_created') {
      setMainSearchCreated(true)
    }

    sessionStorage.removeItem('CASAVO@MAIN_SEARCH')
  }

  return (
    <>
      <Layout contentWidth="default" showFooterOnMobile={false}>
        <div className={suggestedPage}>
          <div className={greetingTitle}>
            <ConceptImage height={52} name="mail-pidgeon" width={52} />
            <Heading1>
              {user?.first_name ? (
                <Translated i18Key="suggested:Suggested_Welcome_Title_Named" values={{ name: user.first_name }} />
              ) : (
                <Translated i18Key="suggested:Suggested_Welcome_Title" />
              )}
            </Heading1>
          </div>

          <div
            className={sprinkles({
              alignItems: { desktop: 'center' },
              display: { desktop: 'flex' },
              justifyContent: { desktop: 'space-between' },
              marginBottom: { desktop: 'l' },
            })}
          >
            {hasCriteriaProvided && (
              <div
                className={sprinkles({
                  display: 'flex',
                  justifyContent: { desktop: 'flex-start', mobile: 'space-between' },
                })}
              >
                <div
                  className={sprinkles({ marginRight: 'xl' })}
                  style={{
                    minWidth: 0, //it makes ellipisis work
                  }}
                >
                  <CriteriaList criteria={criteria} />
                </div>
                <Button
                  ariaLabel="edit preferences"
                  className={propertiesListButton}
                  Icon={() => <Icon.Tune />}
                  shapeVariant="round"
                  sizeVariant="small"
                  styleVariant="secondary"
                  onClick={() => {
                    router.push(buildRelativeLocalizedLink(suggestedFeedSettingsPathSegment, lang))
                    handleClick(GTMEventCategory.SuggestedPage, 'OpenSettings')
                  }}
                />
              </div>
            )}
          </div>

          {hasCriteriaInferred && <InferredCriteriaBanner />}

          <BodyLarge className={sprinkles({ paddingBottom: '2xl' })} color="greyscale500">
            {t('suggested:Suggested_Welcome_Description')}
          </BodyLarge>

          {(userValuation || storedUserValuation) && isLocaleEnabledFor('mortgageValuation', lang) && (
            <MortgageBannerValuationPageContent
              className={sprinkles({ marginBottom: '2xl' })}
              userValuationSessionId={userValuationSessionId}
            />
          )}

          <PotentialPropertiesList isLoading={isLoading} properties={potentialProperties} />
          <Divider className={sprinkles({ paddingBottom: 'l' })} />
          <ListingsList isLoading={isLoading} listings={listingsDetailCate} />
        </div>
        {(userValuation || storedUserValuation) && (
          <>
            <Divider noVerticalMargins />
            <div
              className={suggestedListingPriceTrigger}
              onClick={() => {
                handleClick(GTMEventCategory.SuggestedPage, 'OpenSuggestedListingPrice')
                handleSuggestedListingPriceClick()
              }}
            >
              <BodyM>{t('suggested:ValuationModal_Title_1')}</BodyM>
              <Image height={16} src={`${imageBasePath}/images/plus.svg`} width={16} />
            </div>
          </>
        )}
        <footer>
          <MobileBottomContainerFixed>
            <MobileBottomMenu activeButton="suggested" />
          </MobileBottomContainerFixed>
        </footer>

        <ToastSuccess setSuccess={setShowToastSuccess} success={showToastSuccess} title={toastSuccessTitle} />

        <ToastError error={showToastError} setError={setShowToastError} />
      </Layout>
    </>
  )
}
